import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { PageHeader } from "@/components/Primitives";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Mail, CheckCircle2, AlertTriangle, Database, Cog, ExternalLink, ShieldCheck } from "lucide-react";

export default function Integrations() {
  const [ms, setMs] = useState(null);
  const [guide, setGuide] = useState(false);
  const [params, setParams] = useSearchParams();

  const load = () => api.get("/integrations/outlook/status").then((r) => setMs(r.data));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const o = params.get("outlook");
    if (o === "connected") toast.success("Outlook connected");
    if (o === "error" || o === "expired") toast.error("Outlook connection was not completed");
    if (o) { setParams({}, { replace: true }); load(); }
  }, [params]);

  const connect = () => {
    if (!ms?.configured) { setGuide(true); return; }
    const base = process.env.REACT_APP_BACKEND_URL;
    // same-origin navigation sends the auth cookie; backend 302s to Microsoft
    window.location.href = `${base}/api/integrations/outlook/connect`;
  };

  const disconnect = async () => {
    await api.post("/integrations/outlook/disconnect");
    toast.success("Outlook disconnected"); load();
  };

  return (
    <div data-testid="page-integrations">
      <PageHeader title="Integrations" subtitle="Connect external services and monitor system health." />
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-lg bg-[#0078D4]/10 flex items-center justify-center"><Mail className="h-5 w-5 text-[#0078D4]" /></div>
              <div><h3 className="font-display font-semibold text-slate-800">Microsoft Outlook</h3><p className="text-xs text-slate-400">Email & calendar sync via Microsoft Graph</p></div>
            </div>
            <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold ${ms?.connected ? "bg-[#9ACC32]/15 text-[#5B880C]" : "bg-amber-100 text-amber-700"}`} data-testid="outlook-status">
              {ms?.connected ? <CheckCircle2 className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />}{ms?.status || "Loading"}
            </span>
          </div>
          <p className="text-sm text-slate-500 mt-4">{ms?.note}</p>
          {ms?.email && <p className="text-xs text-slate-500 mt-2">Connected as <strong>{ms.email}</strong></p>}
          <div className="mt-4 flex gap-2">
            {ms?.connected ? (
              <Button variant="outline" onClick={disconnect} data-testid="disconnect-outlook">Disconnect</Button>
            ) : (
              <Button onClick={connect} data-testid="connect-outlook">Connect Outlook</Button>
            )}
            <Button variant="outline" onClick={() => setGuide(true)} data-testid="outlook-setup-guide">Setup Instructions <ExternalLink className="h-4 w-4" /></Button>
          </div>
          {ms?.requires_setup && (
            <p className="text-xs text-amber-600 mt-3 flex items-center gap-1.5"><AlertTriangle className="h-3.5 w-3.5" />Requires Azure App credentials before it can be enabled. Click Setup Instructions.</p>
          )}
          <div className="mt-4 flex flex-wrap gap-1.5">
            {(ms?.scopes || []).map((s) => <span key={s} className="text-[11px] bg-slate-100 text-slate-600 rounded px-2 py-0.5 font-mono">{s}</span>)}
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <h3 className="font-display font-semibold text-slate-800 mb-4 flex items-center gap-2"><Cog className="h-4 w-4 text-[#00C9E0]" />System Status</h3>
          <div className="space-y-3">
            {[["Database", Database], ["Background Jobs", Cog], ["Email Logging (Graph)", Mail], ["Calendar Sync (Graph)", ShieldCheck]].map(([label, Ic]) => (
              <div key={label} className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-2 text-slate-600"><Ic className="h-4 w-4" />{label}</span>
                <span className="inline-flex items-center gap-1.5 text-[#5B880C] font-medium"><span className="h-2 w-2 rounded-full bg-[#9ACC32]" />Ready</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-400 mt-4">Sync architecture (OAuth, token refresh, webhooks, subscription renewal, delta reconciliation, idempotency) is implemented and activates on connection.</p>
        </div>
      </div>

      <Dialog open={guide} onOpenChange={setGuide}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="outlook-guide">
          <DialogHeader><DialogTitle className="font-display">Microsoft Outlook — Azure Setup Checklist</DialogTitle><DialogDescription>Follow these exact steps to enable Outlook email & calendar sync. We request least-privilege permissions only.</DialogDescription></DialogHeader>
          <ol className="text-sm text-slate-600 space-y-2.5 list-decimal pl-5 py-2">
            <li>Go to <strong>entra.microsoft.com</strong> → <strong>Entra ID</strong> → <strong>App registrations</strong> → <strong>New registration</strong>. Choose <em>Accounts in this organizational directory only</em>. Register.</li>
            <li>On <strong>Overview</strong>, copy the <strong>Application (client) ID</strong> → this is your <code className="bg-slate-100 px-1 rounded">MS_CLIENT_ID</code>. Also copy <strong>Directory (tenant) ID</strong> → <code className="bg-slate-100 px-1 rounded">MS_TENANT</code>.</li>
            <li><strong>Authentication</strong> → <strong>Add a platform</strong> → <strong>Web</strong>. Add this exact Redirect URI:
              <div className="bg-slate-100 rounded p-2 mt-1 font-mono text-xs break-all">https://crm.chitchatlocal.ca/api/integrations/outlook/callback</div>
              (and your preview URL callback for testing).</li>
            <li><strong>API permissions</strong> → <strong>Add a permission</strong> → <strong>Microsoft Graph</strong> → <strong>Delegated</strong>. Add only:
              <div className="mt-1 flex flex-wrap gap-1.5">{["Mail.ReadBasic", "Calendars.ReadWrite", "offline_access", "openid", "profile"].map((s) => <span key={s} className="text-[11px] bg-[#00C9E0]/10 text-[#0094A8] rounded px-2 py-0.5 font-mono">{s}</span>)}</div>
              <span className="text-xs text-slate-400">No Mail.Send, no application permissions, no admin-wide scopes.</span></li>
            <li><strong>Certificates & secrets</strong> → <strong>New client secret</strong> → copy the <strong>Value</strong> immediately → this is <code className="bg-slate-100 px-1 rounded">MS_CLIENT_SECRET</code>.</li>
            <li>Enter the values in Emergent: open the <strong>Deployment / Secrets</strong> panel and set
              <code className="bg-slate-100 px-1 rounded mx-1">MS_CLIENT_ID</code>,
              <code className="bg-slate-100 px-1 rounded mx-1">MS_CLIENT_SECRET</code>,
              <code className="bg-slate-100 px-1 rounded mx-1">MS_TENANT</code>. Save & restart the backend.</li>
            <li>Return here — status flips to <strong>Not Connected → Connect Outlook</strong>. Each staff member clicks <strong>Connect Outlook</strong> to link their own mailbox. Passwords are never stored.</li>
          </ol>
        </DialogContent>
      </Dialog>
    </div>
  );
}
